import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, MessageSquarePlus, Layers } from "lucide-react";
import HudFrame from "@/components/primitives/HudFrame";
import SmithersAvatar from "@/components/portal/smithers/SmithersAvatar";
import { pickSmithersSuggestion } from "@/lib/smithers/suggestionEngine";
import { watchOnline } from "@/lib/sync/localStore";

/**
 * SmithersPresencePanel — persistent ecosystem intelligence presence.
 *
 * NOT a chatbot box.
 * Holographic geometric avatar (cyan octahedron + rings) + contextual suggestion
 * line driven by `pickSmithersSuggestion(user, progress)` + 2-3 dynamic action chips.
 *
 * Layout:
 *   desktop: ~280px right rail
 *   mobile:  full-width card inside main column
 *
 * Offline state: portrait dims, mode flips to "PRERECORDED", showing a static line.
 */
export default function SmithersPresencePanel({ user, progress, onStake }) {
    const navigate = useNavigate();
    const [online, setOnline] = useState(true);
    const [askOpen, setAskOpen] = useState(false);

    useEffect(() => {
        const unsub = watchOnline(({ online: o }) => setOnline(o));
        return unsub;
    }, []);

    const mode = online ? "present" : "prerecorded";
    const suggestion = pickSmithersSuggestion({ user, progress });

    // Stake chip gated on Asset Workspace+ entitlement
    const ent = user?.entitlements || {};
    const active = ent.active_modules || [];
    const stakeEligible = Boolean(
        onStake && (
            active.includes("AGENT_ASSET_WORKSPACE")
            || ent.access_type === "full"
            || ["system", "genesis"].includes(ent.tier)
        )
    );

    const offlineLine = "Prerecorded guidance active. Reconnect for live ecosystem suggestions.";
    const displayLine = online ? suggestion.line : offlineLine;

    return (
        <div data-testid="smithers-presence-panel">
            <HudFrame
                bracketColor="var(--tca-cyan-core)"
                bracketSize={14}
                borderTint="rgba(0,219,255,0.20)"
                className="bg-tca-bg-panel/75 backdrop-blur-md relative overflow-hidden"
                innerClassName="p-5"
            >
                {/* Ambient particle wash */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background: online
                            ? "radial-gradient(ellipse at 50% 0%, rgba(0,219,255,0.12), transparent 60%)"
                            : "radial-gradient(ellipse at 50% 0%, rgba(0,219,255,0.05), transparent 60%)",
                    }}
                />

                {/* Eyebrow */}
                <div className="relative z-10 flex items-center justify-between">
                    <span className="font-mono-tight text-[10px] uppercase tracking-[0.34em] text-tca-cyan/90">
                        // Smithers XI
                    </span>
                    <span className="font-mono-tight text-[9px] uppercase tracking-[0.22em] text-tca-ink-muted">
                        {online ? "Live" : "Prerecorded"}
                    </span>
                </div>

                {/* Avatar */}
                <div className="relative z-10 mt-3 flex justify-center">
                    <SmithersAvatar mode={mode} size={172} />
                </div>

                {/* Status */}
                <div className="relative z-10 mt-3 grid grid-cols-2 gap-1 font-mono-tight text-[9px] uppercase tracking-[0.26em] text-tca-ink-muted">
                    <span className="text-tca-cyan/70">// status:</span>
                    <span className="text-tca-cyan-hi text-right">{online ? "PRESENT" : "STANDBY"}</span>
                    <span className="text-tca-cyan/70">// mode:</span>
                    <span className="text-tca-cyan-hi text-right">{online ? "GUIDANCE" : "PRERECORDED"}</span>
                </div>

                {/* Suggestion line */}
                <p
                    className="relative z-10 mt-4 text-[13.5px] leading-snug text-tca-ink italic"
                    data-testid="smithers-suggestion-line"
                >
                    “{displayLine}”
                </p>

                {/* Action chips */}
                <div className="relative z-10 mt-5 space-y-1.5">
                    {suggestion.action && (
                        <ChipButton
                            data-testid="smithers-chip-primary"
                            label={suggestion.action.label}
                            onClick={() => navigate(suggestion.action.route)}
                        />
                    )}
                    {suggestion.secondary && (
                        <ChipButton
                            data-testid="smithers-chip-secondary"
                            label={suggestion.secondary.label}
                            onClick={() => navigate(suggestion.secondary.route)}
                        />
                    )}
                    {stakeEligible && (
                        <ChipButton
                            data-testid="smithers-chip-stake"
                            label="Render via Stake"
                            icon={<Layers size={11} />}
                            onClick={onStake}
                        />
                    )}
                    <ChipButton
                        data-testid="smithers-chip-ask"
                        label="Ask Smithers"
                        icon={<MessageSquarePlus size={11} />}
                        onClick={() => setAskOpen(true)}
                    />
                </div>

                {/* Footer */}
                <div className="relative z-10 mt-5 pt-3 border-t border-tca-cyan/15 flex items-center justify-between font-mono-tight text-[9px] uppercase tracking-[0.26em]">
                    <span className="flex items-center gap-2 text-tca-ink-muted">
                        <motion.span
                            animate={{ opacity: online ? [0.55, 1, 0.55] : 0.4 }}
                            transition={{ duration: 2.2, repeat: Infinity }}
                            className={`inline-block w-1.5 h-1.5 rounded-full ${online ? "bg-tca-cyan shadow-[0_0_8px_rgba(0,219,255,0.7)]" : "bg-tca-ink-muted"}`}
                        />
                        ecosystem · {online ? "online" : "offline"}
                    </span>
                    <span className="text-tca-cyan/60">XI</span>
                </div>
            </HudFrame>

            <AnimatePresence>
                {askOpen && <AskDrawer onClose={() => setAskOpen(false)} />}
            </AnimatePresence>
        </div>
    );
}

function ChipButton({ label, icon, onClick, "data-testid": dt }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={dt}
            className="
                group w-full flex items-center justify-between gap-2
                px-3 py-2 border border-tca-cyan/22 hover:border-tca-cyan/55
                bg-gradient-to-r from-tca-cyan/[0.04] to-transparent
                hover:from-tca-cyan/10
                text-[11px] tracking-tight text-tca-ink
                transition-all duration-300
            "
        >
            <span className="flex items-center gap-2 truncate">
                {icon}
                {label}
            </span>
            <ChevronRight size={11} className="text-tca-cyan/70 group-hover:text-tca-cyan-hi transition-transform group-hover:translate-x-0.5" />
        </button>
    );
}

function AskDrawer({ onClose }) {
    const lines = [
        "// initializing smithers conversation layer",
        "// loading doctrine context",
        "// ecosystem handshake pending",
    ];
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            data-testid="smithers-ask-drawer"
            onClick={onClose}
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-tca-bg-deep/72 backdrop-blur-sm p-4"
        >
            <motion.div
                initial={{ scale: 0.96, y: 16 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.96, y: 16 }}
                onClick={(e) => e.stopPropagation()}
                className="max-w-md w-full border border-tca-cyan/30 bg-tca-bg-panel/95 backdrop-blur-xl p-6 sm:p-7"
                style={{ boxShadow: "0 0 48px rgba(0,219,255,0.18)" }}
            >
                <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/80">
                    // Smithers · Conversation Layer
                </div>
                <div className="mt-3 font-display text-2xl text-tca-ink">
                    Initializing soon.
                </div>
                <p className="mt-2 text-sm text-tca-ink-muted leading-relaxed">
                    Live Smithers conversation is preparing — pre-recorded guidance is already shipping inside your System Package. The conversational layer activates in the next ecosystem release.
                </p>
                <div className="mt-5 space-y-1 font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-cyan/85">
                    {lines.map((l, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -6 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.15 + i * 0.15, duration: 0.22 }}
                        >
                            {l}
                            <DotsAnim />
                        </motion.div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="smithers-ask-close"
                    className="mt-6 font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-cyan/85 hover:text-tca-cyan border border-tca-cyan/35 hover:border-tca-cyan/65 px-4 py-2 rounded-full transition-all"
                >
                    Close
                </button>
            </motion.div>
        </motion.div>
    );
}

function DotsAnim() {
    return (
        <motion.span
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.4, repeat: Infinity }}
            className="ml-1"
        >
            ...
        </motion.span>
    );
}
